import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronUp, ArrowRight, Check } from 'lucide-react';
import api from '../lib/api';
import SEO from '../components/SEO';
import { prepareContent } from '../lib/content';

export default function Services() {
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [expandedSlug, setExpandedSlug] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/service-categories'),
      api.get('/services')
    ]).then(([catRes, svcRes]) => {
      setCategories(catRes.data);
      setPackages(svcRes.data);
    }).catch(() => {});
  }, []);

  const tierStyles = {
    bronze: { accent: '#B08D6A', badge: 'bg-[#B08D6A]/10 text-[#B08D6A]' },
    silver: { accent: '#94A3B8', badge: 'bg-[#94A3B8]/10 text-[#94A3B8]' },
    gold: { accent: '#C5A059', badge: 'bg-[#C5A059]/20 text-[#C5A059]' },
    vip: { accent: '#0B1B3D', badge: 'bg-[#0B1B3D]/10 text-[#0B1B3D]' },
  };

  const handleToggle = (slug) => {
    setExpandedSlug(prev => prev === slug ? null : slug);
  };

  return (
    <div data-testid="services-page">
      <SEO
        title="Szolgáltatások"
        description="Könyvelési szolgáltatások, átvilágítás és kintlévőség kezelés. Válasszon az igényeinek megfelelő megoldásból."
      />

      {/* Hero */}
      <section className="py-20 sm:py-28 bg-[#0B1B3D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Szolgáltatásaink</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-['Outfit'] font-light text-white tracking-tight">
            Megoldások <span className="font-semibold">vállalkozásának</span>
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Professzionális pénzügyi szolgáltatások, amelyekre építhet.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {categories.map((cat) => {
              const isExpanded = expandedSlug === cat.slug;
              return (
                <div key={cat.id} data-testid={`category-${cat.slug}`}>
                  {/* Category Item */}
                  <button
                    onClick={() => handleToggle(cat.slug)}
                    data-testid={`category-btn-${cat.slug}`}
                    className={`w-full text-left group transition-all duration-300 rounded-lg border p-6 sm:p-8 ${
                      isExpanded
                        ? 'bg-[#0B1B3D] border-[#0B1B3D] shadow-xl'
                        : 'bg-white border-[#E2E8F0] hover:-translate-y-0.5 hover:shadow-lg hover:border-[#C5A059]/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h2 className={`text-2xl sm:text-3xl font-['Outfit'] font-semibold tracking-tight transition-colors duration-300 ${
                          isExpanded ? 'text-white' : 'text-[#0B1B3D] group-hover:text-[#0B1B3D]'
                        }`}>
                          {cat.title}
                        </h2>
                        <p className={`mt-2 text-sm sm:text-base transition-colors duration-300 ${
                          isExpanded ? 'text-[#94A3B8]' : 'text-[#64748B]'
                        }`}>
                          {cat.description}
                        </p>
                      </div>
                      <div className={`ml-4 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isExpanded ? 'bg-[#C5A059] text-[#0B1B3D]' : 'bg-[#0B1B3D]/5 text-[#0B1B3D] group-hover:bg-[#C5A059]/10'
                      }`}>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-4 animate-fade-in">
                      {cat.has_packages ? (
                        /* Accounting Packages */
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {packages.map((svc) => {
                            const style = tierStyles[svc.tier] || tierStyles.bronze;
                            const isGold = svc.highlighted;
                            return (
                              <div
                                key={svc.id}
                                data-testid={`service-card-${svc.tier}`}
                                className={`relative rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl ${
                                  isGold
                                    ? 'bg-[#0B1B3D] border-2 border-[#C5A059]/30'
                                    : 'bg-white border border-[#E2E8F0]'
                                }`}
                              >
                                {isGold && (
                                  <div className="absolute top-0 right-0">
                                    <div className="bg-[#C5A059] text-[#0B1B3D] text-xs font-bold px-4 py-1.5 rounded-bl-lg">
                                      Legnépszerűbb
                                    </div>
                                  </div>
                                )}
                                <div className="p-6 sm:p-8">
                                  <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${style.badge}`}>
                                    {svc.price_label || svc.tier.toUpperCase()}
                                  </div>
                                  <h3 className={`text-2xl font-['Outfit'] font-semibold mb-3 ${isGold ? 'text-white' : 'text-[#0B1B3D]'}`}>
                                    {svc.name}
                                  </h3>
                                  <p className={`text-sm mb-6 ${isGold ? 'text-[#94A3B8]' : 'text-[#64748B]'}`}>
                                    {svc.description}
                                  </p>
                                  <ul className="space-y-2 mb-6">
                                    {svc.features.map((f, i) => (
                                      <li key={i} className={`flex items-start gap-2 text-sm ${isGold ? 'text-[#94A3B8]' : 'text-[#64748B]'}`}>
                                        <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: style.accent }} />
                                        {f}
                                      </li>
                                    ))}
                                  </ul>
                                  <Link to="/kapcsolat">
                                    <Button
                                      data-testid={`service-${svc.tier}-cta`}
                                      className={`w-full ${
                                        isGold
                                          ? 'bg-[#C5A059] hover:bg-[#B08D4A] text-[#0B1B3D] font-semibold'
                                          : 'bg-[#0B1B3D] hover:bg-[#162B56] text-white'
                                      }`}
                                    >
                                      Érdekel <ArrowRight className="ml-2" size={16} />
                                    </Button>
                                  </Link>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Content for non-package categories */
                        <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 sm:p-10">
                          <div
                            className="prose prose-lg max-w-none text-[#1A1F2C]"
                            dangerouslySetInnerHTML={{ __html: prepareContent(cat.content) }}
                          />
                          {cat.content && !cat.content.includes('Feltöltés alatt') && (
                            <div className="mt-8">
                              <Link to="/kapcsolat">
                                <Button className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
                                  Érdeklődöm <ArrowRight className="ml-2" size={16} />
                                </Button>
                              </Link>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-['Outfit'] font-semibold text-[#0B1B3D]">
            Nem tudja, melyik szolgáltatás a megfelelő?
          </h2>
          <p className="mt-4 text-base text-[#64748B]">
            Segítünk kiválasztani az Önnek legmegfelelőbb megoldást. Kérjen ingyenes konzultációt!
          </p>
          <Link to="/kapcsolat" className="inline-block mt-8">
            <Button size="lg" data-testid="services-cta-btn" className="bg-[#C5A059] hover:bg-[#B08D4A] text-[#0B1B3D] font-semibold px-10">
              Ingyenes konzultáció <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
